import { useEffect, useRef, useState } from 'react';

function getVideoProgress(videoEl) {
  if (!videoEl || !Number.isFinite(videoEl.duration) || videoEl.duration <= 0) {
    return null;
  }

  return Math.max(0, Math.min(1, videoEl.currentTime / videoEl.duration));
}

function setProgressFill(progressFillRef, progress) {
  if (!progressFillRef.current) {
    return;
  }

  progressFillRef.current.style.transform = `scaleX(${progress})`;
}

export default function useVideoProgressBar(currentIndex, isCurrentVideo) {
  const [readyVideoIndex, setReadyVideoIndex] = useState(null);
  const videoRefs = useRef({});
  const progressFillRef = useRef(null);
  const playbackVersionRef = useRef(0);
  const isCurrentVideoReady = isCurrentVideo && readyVideoIndex === currentIndex;

  const updateVideoProgress = (videoEl) => {
    if (videoEl !== videoRefs.current[currentIndex]) {
      return;
    }

    const progress = getVideoProgress(videoEl);
    if (progress === null) {
      return;
    }

    setProgressFill(progressFillRef, progress);
  };

  useEffect(() => {
    playbackVersionRef.current += 1;
    const playbackVersion = playbackVersionRef.current;

    Object.entries(videoRefs.current).forEach(([key, videoEl]) => {
      if (Number(key) !== currentIndex && videoEl && !videoEl.paused) {
        videoEl.pause();
      }
    });

    setProgressFill(progressFillRef, 0);

    if (!isCurrentVideo) {
      setReadyVideoIndex(null);
      return undefined;
    }

    setReadyVideoIndex(null);

    const videoEl = videoRefs.current[currentIndex];
    if (!videoEl) {
      return undefined;
    }

    const isStale = () => playbackVersionRef.current !== playbackVersion;
    const markReady = () => {
      if (isStale() || videoEl.seeking) {
        return;
      }

      setReadyVideoIndex(currentIndex);
      const progress = getVideoProgress(videoEl);
      if (progress !== null) {
        setProgressFill(progressFillRef, progress);
      }
    };

    const attemptPlay = () => {
      if (isStale()) {
        return;
      }

      // iOS WebKit (every iOS browser, Chrome included) ignores preload="auto"
      // and refuses to buffer media data until a play() request, so readyState
      // sticks at HAVE_METADATA and canplay/loadeddata never fire on their own.
      // Request playback unconditionally — the play() call is what starts
      // buffering — and let the 'playing' event flip the loading overlay off.
      videoEl.play().catch(() => { /* autoplay blocked, ignore */ });
      if (videoEl.readyState >= 2) {
        markReady();
      }
    };

    const seekToStart = () => {
      if (isStale()) {
        return;
      }

      setProgressFill(progressFillRef, 0);

      try {
        if (videoEl.currentTime !== 0) {
          videoEl.currentTime = 0;
        }
      } catch (error) {
        // Some browsers reject seeking before metadata has loaded.
      }

      attemptPlay();
    };

    videoEl.pause();
    videoEl.addEventListener('loadedmetadata', seekToStart);
    videoEl.addEventListener('loadeddata', attemptPlay);
    videoEl.addEventListener('canplay', attemptPlay);
    videoEl.addEventListener('seeked', attemptPlay);
    videoEl.addEventListener('playing', markReady);

    if (videoEl.readyState >= 1) {
      seekToStart();
    } else {
      videoEl.load();
      attemptPlay();
    }

    return () => {
      videoEl.removeEventListener('loadedmetadata', seekToStart);
      videoEl.removeEventListener('loadeddata', attemptPlay);
      videoEl.removeEventListener('canplay', attemptPlay);
      videoEl.removeEventListener('seeked', attemptPlay);
      videoEl.removeEventListener('playing', markReady);
    };
  }, [currentIndex, isCurrentVideo]);

  useEffect(() => {
    if (
      !isCurrentVideo ||
      !isCurrentVideoReady ||
      typeof window === 'undefined' ||
      typeof window.requestAnimationFrame !== 'function'
    ) {
      return undefined;
    }

    let animationFrameId = null;
    const tick = () => {
      const progress = getVideoProgress(videoRefs.current[currentIndex]);
      if (progress !== null) {
        setProgressFill(progressFillRef, progress);
      }
      animationFrameId = window.requestAnimationFrame(tick);
    };

    tick();

    return () => {
      if (animationFrameId !== null) {
        window.cancelAnimationFrame(animationFrameId);
      }
    };
  }, [currentIndex, isCurrentVideo, isCurrentVideoReady]);

  return {
    isCurrentVideoReady,
    progressFillRef,
    updateVideoProgress,
    videoRefs,
  };
}
